import { CSSProperties, useEffect, useState } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { getStageFunnel } from '../../../../Services/CRMAdminServices/CRMDashboardService';

interface FunnelRow {
    stage: string;
    stageCode: string | null;
    inStageNow: number;
    enteredInPeriod: number;
    reached: number;
    pctOfLeads: string;
    pctFromPrevious: string;
    avgDays: string;
}
interface FunnelGroup { name: string; stages: FunnelRow[]; }
interface FunnelResponse {
    fromDate: string;
    toDate: string;
    leadsCreated: number;
    leadsTotal: number;
    stageChangesInPeriod: number;
    historySince: string | null;
    groups: FunnelGroup[];
    note: string | null;
}

const GROUP_TITLE: Record<string, string> = {
    Common: 'Enquiry → Gameplan',
    MB: 'Mortgage Broking',
    IP: 'Investment Property',
    Other: 'Not Ready / Lost',
};
const GROUP_COLOUR: Record<string, string> = {
    Common: '#0044ff',
    MB: '#00b050',
    IP: '#ff7a00',
    Other: '#888888',
};

// traffic light on "% from previous": green >= 60, orange >= 40, red below; grey when unknown
const pctColour = (pct: string) => {
    const v = parseFloat(pct);
    if (isNaN(v)) return '#bbbbbb';
    if (v >= 60) return '#00b050';
    if (v >= 40) return '#FFA500';
    return '#ff0000';
};

const cell: CSSProperties = { padding: '6px 8px', fontSize: '15px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' };
const head: CSSProperties = { ...cell, fontSize: '12px', fontWeight: 700, color: '#555', borderBottom: '2px solid #e0e0e0' };

/**
 * Dashboard stage funnel. Every stage in the CRM's stage list, grouped by the
 * prefix in the stage name (MB - / IP -), with:
 *   Now       - leads sitting on the stage today
 *   Entered   - leads that moved into the stage during the period
 *   Reached   - leads created in the period that got to this stage (cohort)
 *   % leads   - Reached as a share of leads created in the period
 *   % prev    - Reached as a share of the previous stage (conversion)
 *   Avg days  - average time a lead spends on the stage
 */
const StageFunnel = ({ fromDate, toDate }: { fromDate: string; toDate: string }) => {
    const [data, setData] = useState<FunnelResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true); setError(null);
            try {
                const res = await getStageFunnel(fromDate, toDate);
                if (!cancelled) setData(res?.data ?? null);
            } catch (e) {
                console.error('Error fetching stage funnel:', e);
                if (!cancelled) { setData(null); setError('Unable to load the stage funnel right now.'); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [fromDate, toDate]);

    return (
        <Card className="shadow-sm border-0 mb-3">
            <CardBody className="p-4">
                <div className="mb-2 d-flex align-items-baseline gap-3 flex-wrap">
                    <h3 className="fw-bold mb-1" style={{ color: '#0044ff' }}>Stage Funnel</h3>
                    {data && (
                        <span className="fw-bold mb-1" style={{ color: '#0044ff', fontSize: '18px' }}>
                            Total Leads {data.leadsTotal}
                        </span>
                    )}
                </div>

                {error && <div className="text-danger mb-2">{error}</div>}
                {loading ? (
                    <div className="text-center text-muted py-4">Loading stage funnel...</div>
                ) : !data ? (
                    <div className="text-center text-muted py-4">No stage data available.</div>
                ) : (
                    <Row className="g-3">
                        {data.groups.map((g) => (
                            <Col xs={12} lg={g.name === 'Common' || g.name === 'Other' ? 12 : 6} key={g.name}>
                                <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                                    <div style={{ background: GROUP_COLOUR[g.name] || '#0044ff', color: '#fff', padding: '6px 12px', fontWeight: 700, fontSize: '15px' }}>
                                        {GROUP_TITLE[g.name] || g.name}
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...head, textAlign: 'left' }}>Stage</th>
                                                    <th style={{ ...head, textAlign: 'right' }} title="Leads on this stage today">Now</th>
                                                    <th style={{ ...head, textAlign: 'right' }} title="Leads that moved into this stage in the period">Entered</th>
                                                    <th style={{ ...head, textAlign: 'right' }} title="Leads created in the period that reached this stage">Reached</th>
                                                    <th style={{ ...head, textAlign: 'right' }} title="Reached ÷ leads created in the period">% Leads</th>
                                                    <th style={{ ...head, textAlign: 'right' }} title="Reached ÷ previous stage">% Prev</th>
                                                    <th style={{ ...head, textAlign: 'right' }} title="Average days a lead spends on this stage">Avg Days</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {g.stages.map((s) => (
                                                    <tr key={s.stageCode || s.stage}>
                                                        <td style={{ ...cell, fontWeight: 600 }}>{s.stage}</td>
                                                        <td style={{ ...cell, textAlign: 'right' }}>{s.inStageNow}</td>
                                                        <td style={{ ...cell, textAlign: 'right' }}>{s.enteredInPeriod}</td>
                                                        <td style={{ ...cell, textAlign: 'right' }}>{s.reached}</td>
                                                        <td style={{ ...cell, textAlign: 'right' }}>{s.pctOfLeads}</td>
                                                        <td style={{ ...cell, textAlign: 'right' }}>
                                                            {s.pctFromPrevious}
                                                            {g.name !== 'Other' && s.pctFromPrevious !== '-' && (
                                                                <span style={{ color: pctColour(s.pctFromPrevious), fontSize: '18px', marginLeft: '6px' }}>●</span>
                                                            )}
                                                        </td>
                                                        <td style={{ ...cell, textAlign: 'right' }}>{s.avgDays}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default StageFunnel;
